import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAlertRuleEvents } from "@/hooks/queries";
import { gpsDriveNotificationSoundsService } from "@/services/gpsdrive-notification-sounds.service";
import { notificationSoundsKey } from "@/hooks/queries/platform";

// Toca o áudio configurado pelo Super Master (/master/notifications) para
// o TIPO de um AlertEvent novo, enquanto o app da empresa está aberto —
// única forma real de um som customizado tocar (push do navegador/SO não
// permite som por tipo, ver comentário em notification-sounds.controller.ts
// no backend). Mesma fonte de dados do sino (useAlertRuleEvents, poll de
// 30s) — não abre um canal novo, só reage a IDs que ainda não tinha visto.
//
// Só toca alerta que DISPAROU DEPOIS que o app abriu (ou que o usuário
// entrou). Bug real 2026-10-03 (cliente com 2 veículos, app Android): ao
// abrir o app tocaram de uma vez todos os "ignição ligada/desligada" que
// tinham acontecido com o app fechado. A antiga "linha d'água" por ID
// usava a PRIMEIRA lista vista — que vem do cache salvo no aparelho
// (persistência do React Query, até 24h) ou de uma resposta incompleta no
// meio da renovação de sessão — e tudo que a busca real trouxe a mais
// parecia "novo". Agora a regra é pelo horário do evento (firedAt), que
// não depende de qual lista chegou primeiro.
//
// Também: no máximo UM som por leitura (poll de 30 s) — vários alertas
// chegando juntos tocam só o mais recente, nunca uma rajada.
const START_SLACK_MS = 10_000; // relógio do aparelho x servidor
const MAX_AGE_MS = 3 * 60_000; // nunca toca alerta com mais de 3 min

export function useNotificationSoundPlayer(enabled: boolean, userKey?: string | number | null) {
  const { data: events } = useAlertRuleEvents();
  const { data: sounds } = useQuery({
    queryKey: notificationSoundsKey,
    queryFn: () => gpsDriveNotificationSoundsService.list(),
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const startedAtRef = useRef<number | null>(null);
  const playedIdsRef = useRef<Set<string>>(new Set());

  // Abriu o app / entrou / trocou de usuário: novo marco de tempo.
  useEffect(() => {
    startedAtRef.current = enabled ? Date.now() : null;
    playedIdsRef.current = new Set();
  }, [enabled, userKey]);

  useEffect(() => {
    const startedAt = startedAtRef.current;
    if (!enabled || startedAt === null || !events?.length) return;

    const now = Date.now();
    const fresh = events
      .filter((e) => {
        if (playedIdsRef.current.has(e.id)) return false;
        const t = Date.parse(e.firedAt);
        return Number.isFinite(t) && t >= startedAt - START_SLACK_MS && now - t <= MAX_AGE_MS;
      })
      .sort((a, b) => Date.parse(b.firedAt) - Date.parse(a.firedAt));
    if (!fresh.length) return;
    for (const e of fresh) playedIdsRef.current.add(e.id);

    const soundByType = new Map((sounds ?? []).map((s) => [s.alertKey, s.url]));
    const url = fresh.map((e) => soundByType.get(e.type)).find(Boolean);
    if (!url) return;
    new Audio(url).play().catch(() => {
      // Autoplay bloqueado (sem interação do usuário ainda) ou arquivo
      // inacessível — silencioso, o alerta já aparece no sino/toast de
      // qualquer forma, o som é só um reforço.
    });
  }, [enabled, events, sounds]);
}
